import { useEffect, useState } from "react";
import api from "../lib/api";
import { Clock, CheckCircle2, AlertOctagon, Scan, Banknote, FileText, ChevronDown, ChevronUp } from "lucide-react";

const ICONS = {
  submitted: FileText,
  ocr_screening: Scan,
  decision: CheckCircle2,
  manual_review: AlertOctagon,
  rejection: AlertOctagon,
  payout: Banknote,
  audit: Clock,
};

const TONE = {
  submitted: "bg-[#114232]/10 text-[#114232] border-[#114232]/30",
  ocr_screening: "bg-[#114232]/8 text-[#114232] border-[#114232]/25",
  decision: "bg-[#114232]/12 text-[#114232] border-[#114232]/40",
  manual_review: "bg-[#d49a36]/15 text-[#8a5a16] border-[#d49a36]/40",
  rejection: "bg-[#c15337]/12 text-[#8b3a23] border-[#c15337]/35",
  payout: "bg-[#114232]/14 text-[#114232] border-[#114232]/40",
  audit: "bg-[#5c6360]/10 text-[#5c6360] border-[#5c6360]/25",
};

/**
 * Vertical timeline of events for a single claim. Pulls from
 * GET /api/claims/{id}/timeline (auth-aware: merchant gets their own, staff
 * gets any).
 */
export default function ClaimTimeline({ claimId, defaultExpanded = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(defaultExpanded);

  useEffect(() => {
    if (!claimId || (!open && !defaultExpanded)) return;
    api.get(`/claims/${claimId}/timeline`)
      .then((r) => setData(r.data))
      .catch(() => setData({ events: [], count: 0 }));
  }, [claimId, open, defaultExpanded]);

  if (!claimId) return null;

  return (
    <div className="bg-white border border-[#114232]/10 rounded-md" data-testid={`claim-timeline-${claimId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`claim-timeline-toggle-${claimId}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#114232]/5 text-left"
      >
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#114232]">
          Status timeline {data ? `· ${data.count} event${data.count === 1 ? "" : "s"}` : ""}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-[#5c6360]" /> : <ChevronDown className="w-4 h-4 text-[#5c6360]" />}
      </button>
      {open && (
        <div className="p-4 border-t border-[#114232]/10" data-testid={`claim-timeline-body-${claimId}`}>
          {!data ? (
            <div className="text-xs text-[#5c6360]">Loading timeline…</div>
          ) : data.events.length === 0 ? (
            <div className="text-xs text-[#5c6360]">No events yet.</div>
          ) : (
            <ol className="relative space-y-4 pl-6 before:absolute before:left-2 before:top-1 before:bottom-1 before:w-px before:bg-[#114232]/15">
              {data.events.map((e, i) => {
                const Icon = ICONS[e.kind] || Clock;
                const tone = TONE[e.kind] || TONE.audit;
                return (
                  <li key={i} data-testid={`timeline-event-${e.kind}-${i}`} className="relative">
                    <span className={`absolute -left-6 top-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${tone}`}>
                      <Icon className="w-3 h-3" />
                    </span>
                    <div className="text-sm font-medium text-[#1b1d1c]">{e.label}</div>
                    {e.detail && (
                      <div className="text-xs text-[#5c6360] mt-0.5">
                        {typeof e.detail === "string" ? e.detail : JSON.stringify(e.detail)}
                      </div>
                    )}
                    <div className="text-[10px] uppercase tracking-wider text-[#5c6360]/70 font-mono mt-1">
                      {e.at ? new Date(e.at).toLocaleString() : "—"} · {e.actor || "—"}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
